// Toast settings: live position, toast-level mute, and sound playback.
// Sounds play ONLY through the app's existing sound manager — no new audio
// layer, no bundled files, no second audio path.
import { soundManager } from '@/core/audio/sounds/soundManager'
import { TOAST_STATES, DEFAULT_TOAST_POSITION } from '@constants/toastConstants'

// ---------------------------------------------------------------------------
// Position — a tiny external store so <ToastContainer> can track the position
// live via useSyncExternalStore (no remount when the device breakpoint flips).
// ---------------------------------------------------------------------------
let position = DEFAULT_TOAST_POSITION
const positionListeners = new Set()

export const getToastPosition = () => position

export const subscribeToastPosition = (listener) => {
  positionListeners.add(listener)
  return () => positionListeners.delete(listener)
}

export const setToastPosition = (next) => {
  if (!next || next === position) return
  position = next
  positionListeners.forEach((listener) => listener())
}

// ---------------------------------------------------------------------------
// Mute — toast-level mute, persisted across reload. There is intentionally NO
// dedicated toast mute button: the app's global sound mute (soundManager) is
// the primary control and is honoured below. This flag is an extra,
// programmatic override and defaults to false.
// ---------------------------------------------------------------------------
const MUTE_STORAGE_KEY = 'toast:muted'

export const getToastMuted = () => {
  try {
    return localStorage.getItem(MUTE_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

export const setToastMuted = (muted) => {
  try {
    localStorage.setItem(MUTE_STORAGE_KEY, muted ? 'true' : 'false')
  } catch {
    // localStorage unavailable (private mode / SSR) — ignore.
  }
}

// ---------------------------------------------------------------------------
// Sound — resolve a state's app sound key and play it through soundManager.
// notify() calls this for every toast, passing the per-call `sound` override.
//
// Resolution: an explicit `false`/`null` override silences the toast; a string
// override wins; otherwise the state's default sound plays.
// Mute: bails early if the app's global sound is muted OR the toast is muted.
// ---------------------------------------------------------------------------
export function playToastSound(type, soundOverride) {
  // Respect BOTH the app's global sound mute and the toast-level mute.
  // (soundManager.play also no-ops when disabled/locked, but bail explicitly.)
  if (!soundManager.enabled || getToastMuted()) return

  // Explicitly silenced for this call.
  if (soundOverride === false || soundOverride === null) return

  const key = typeof soundOverride === 'string' ? soundOverride : TOAST_STATES[type]?.sound

  if (!key) return // e.g. the loading state default is silent
  soundManager.play(key)
}
